import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './system-check.less';
import './webcam.less'
import { Row, Col } from 'antd';
import SystemCheckProgress from './system-check-progress';
import OctagonExclamation from '../../../assets/octagon-exclamation.svg';

interface IWebcam {
    id: string,
    label: string
}
let cameraRecommended = '';
let audioSource = null;
let selectedCamera = '';
let selectedCameraRes = ''
let constraints = {
    video: { deviceId: audioSource ? { exact: audioSource } : undefined },
}
var stream: MediaStream | null = null;

function Webcam(this: any, props: any) {
    const { webcamStatus, setWebcamStatus, webcamCheckResult } = props;
    const { t } = useTranslation();
    const [rerender, setRerender] = useState(false);
    const [isError, setIsError] = useState(false);
    const [availableWebCam, setAvailableWebcam] = useState<IWebcam[] | null>(null);
    const [webcamCheckedStatus, setWebcamCheckedStatus] = useState(webcamStatus);
    const [errorMessage, setErrorMessage] = useState('');
    const delay = parseInt(process.env.REACT_APP_SYSTEM_CHECK_FAKE_DELAY as string);

    if (isError) {
        setIsError(false);
    }

    useEffect(() => {
        if (!webcamStatus.notStarted && !webcamCheckedStatus.completed ) {
            getCameras(constraints);
        }

        cameraRecommended = process.env.REACT_APP_CAMERA_RESOLUTION_RECOMMENDED as string;
        return () => {
            var videoTrack = stream?.getVideoTracks();
            if (videoTrack) {
                if (videoTrack?.length > 0) {
                    stream?.removeTrack(videoTrack[0])
                }
            }
        }
    }, [rerender, webcamCheckedStatus.inProgress, isError])

    const getCameras = async (constraints: any) => {
        try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoInput = 'videoinput'
            const webcams: Array<IWebcam> = [];
            devices.forEach((device) => {
                if (device.kind.toLocaleLowerCase() === videoInput.toLocaleLowerCase()) {
                    webcams.push({
                        label: device.label,
                        id: device.deviceId
                    })
                }
            })
            
            setAvailableWebcam(webcams);
            handleSuccess(stream);

        } catch (e) {
            handleError(e);
        }
    }

    const handleSuccess = (stream: any) => {
        let video: HTMLVideoElement | null | undefined;
        video = document.querySelector('video');
        const videoTracks = stream.getVideoTracks();
        selectedCamera = videoTracks[0].label;
        let settings = videoTracks[0].getSettings();
        setCameraResolution(settings);

        if (video) {
            video.srcObject = stream;
        }
    }

    const handleError = (error: any) => {
        selectedCameraRes = t('SYSTEM_CHECK_WEBCAM_UNAVAILABLE')
        webcamStatus.notStarted = false;
        webcamStatus.inProgress = false;
        webcamStatus.passed = false;
        webcamStatus.failed = true;
        setWebcamCheckedStatus(webcamStatus);
        setIsError(true);
        setErrorMessage(error.message);
    }

    const onSelect = (ev: any) => {
        audioSource = ev.target.value;
        constraints.video.deviceId = audioSource
        getCameras(constraints);
    }

    const proceedToNextStep = () => {
        webcamStatus.completed = true;
        webcamStatus.inProgress = false;
        if (webcamStatus.failed) {
            webcamStatus.passed = false;
        } else {
            webcamStatus.passed = true;
        }

        setWebcamStatus(webcamStatus);
        setRerender(!rerender);
        webcamCheckResult(selectedCameraRes, selectedCamera, errorMessage);

        stream?.getVideoTracks().forEach(video => video.stop());
    }

    const focusProceedNextBtn = (btn: any) => {
        if (!webcamCheckedStatus.completed) {
            setTimeout(() => btn?.focus(), delay);
        } else {
            btn?.blur();
        }
    }
    
    const focusSelectCamera = (sel: any) => {
        if (availableWebCam && availableWebCam.length > 1) sel?.focus();
    }

    const setCameraResolution = (settings: any) => {
        selectedCameraRes = `${settings.width}px x ${settings.height}px`;
    }

    return (
        <>
            <Row>
                <Col span={1}>
                    <div className='system-check-circle'>4</div>
                    <div className='screen-readers-only'>Step 4 of 6</div>
                </Col>
                <Col span={3} className='system-check-icon with-margin-bottom-24px'>
                    <i className='fa-solid fa-video fa-2xl' aria-hidden='true'></i>
                </Col>
                <Col span={16}>
                    <h3 className='system-check-name'>{t('SYSTEM_CHECK_WEBCAM')}</h3>
                </Col>
                <Col span={4}>
                    <SystemCheckProgress
                        name={t('SYSTEM_CHECK_WEBCAM')}
                        notStarted={webcamCheckedStatus.notStarted}
                        inProgress={webcamCheckedStatus.inProgress}
                        passed={webcamCheckedStatus.passed}
                        failed={webcamCheckedStatus.failed}
                    />
                </Col>
                <Col span={5} offset={2} className={webcamCheckedStatus.notStarted ? 'system-check-result-hide' : 'system-check-result-show'}>
                    <div><b>{t('SYSTEM_CHECK_YOUR_SYSTEM')}:</b></div>
                </Col>
                <Col span={14} className={webcamCheckedStatus.notStarted ? 'system-check-result-hide' : 'system-check-result-show'}>
                    <span>{selectedCameraRes}</span>
                </Col>
                <Col span={5} offset={2} className={webcamCheckedStatus.notStarted || webcamCheckedStatus.passed ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                    {webcamCheckedStatus.passed ? '' : <div><b>{t('SYSTEM_CHECK_RECOMMENDED')}:</b></div>}
                </Col>
                <Col span={14} className={webcamCheckedStatus.notStarted || webcamCheckedStatus.passed ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                    {webcamCheckedStatus.passed ? '' : <div>{cameraRecommended}</div>}
                </Col>
                <Col span={5} offset={2} className={webcamCheckedStatus.notStarted ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                    {webcamCheckedStatus.failed ? '' : <div><b>{t('SYSTEM_CHECK_WEBCAM_SELECTED')}:</b></div>}
                </Col>
                <Col span={14} className={webcamCheckedStatus.notStarted ? 'system-check-result-hide new-row' : 'system-check-result-show new-row with-padding-left-8px with-padding-right-8px'}>
                    {webcamCheckedStatus.failed ? '' : <span>{selectedCamera}</span>}
                </Col>
                <Col span={5} offset={2} className={webcamCheckedStatus.notStarted || webcamCheckedStatus.passed ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                    {(webcamCheckedStatus.passed || webcamCheckedStatus.failed) ? '' : <div className='webcam-preview-text'><b>{t('SYSTEM_CHECK_WEBCAM_PREVIEW')}:</b></div>}
                </Col>
                <Col span={14} className={webcamCheckedStatus.notStarted || webcamCheckedStatus.passed ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                    <div className={(webcamCheckedStatus.passed || webcamCheckedStatus.failed) ? 'webcam-video-section-hide' : 'webcam-video-section-show'}>
                        {(availableWebCam?.length === 1) ? '' : <label htmlFor="camera-select">{t('SYSTEM_CHECK_WEBCAM_SELECT_DESIRED_CAMERA')}</label>}
                        <select id="camera-select" className={(availableWebCam?.length === 1) ? 'camera-select-hide' : 'camera-select'} onChange={onSelect} ref={focusSelectCamera}>
                            {availableWebCam?.map((camera) =>
                                <option
                                    className="option-select"
                                    key={camera.id}
                                    value={camera.id}>{camera.label}
                                </option>)
                            }
                        </select>
                        <video className='webcam-preview-section' autoPlay playsInline></video>
                    </div>
                    {(webcamCheckedStatus.failed && !webcamCheckedStatus.completed) && getErrorDisplay(t('SYSTEM_CHECK_WEBCAM_ERROR_TITLE'), t('SYSTEM_CHECK_WEBCAM_ERROR_MESSAGE'))}
                </Col>
                <Col span={5} offset={2} className={webcamCheckedStatus.notStarted || webcamCheckedStatus.completed ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                </Col>
                <Col span={14} className={webcamCheckedStatus.notStarted || webcamCheckedStatus.completed ? 'system-check-result-hide new-row' : 'system-check-result-show new-row'}>
                    <div className={!webcamCheckedStatus.completed ? 'webcam-btn-section-show' : 'webcam-btn-section-hide'} >
                        <button className='webcam-btn-check' 
                            onClick={proceedToNextStep}
                            ref={focusProceedNextBtn}>
                            <i className="fa-solid fa-arrow-right"></i>
                            {t('SYSTEM_CHECK_PROCEED_NEXT')}
                        </button>
                    </div>
                </Col>
            </Row>
        </>
    )
}

const getErrorDisplay = (title: string, message: string) => {
    return (
        <section id='webcam-error-display'>
            <div className='webcam-error-display-left'>
                <img className='webcam-exclamation-icon' src={OctagonExclamation} aria-hidden="true"  alt="" />
            </div>
            <div className='webcam-error-display-right'>
                <span className='webcam-error-display-right-item'>{title}</span>
                <span className='webcam-error-display-right-item'>{message}</span>
            </div>
        </section>
    )
}
export default Webcam;
